import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2024 Sequent Tech <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useContext, useMemo } from "react";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { AuthContext } from "@/providers/AuthContextProvider";
import { IPermissions } from "@/types/keycloak";
import { calcPrecentage, calcTotalApplications } from "../election-event/stats/ElectionEventStats";
import Stats from "../Stats";
import { useTranslation } from "react-i18next";
var ElectionStats = function (props) {
    var eligibleVotersCount = props.eligibleVotersCount, enrolledVotersCount = props.enrolledVotersCount, approvalStats = props.approvalStats, authenticationStats = props.authenticationStats, votedCount = props.votedCount;
    var t = useTranslation().t;
    var authContext = useContext(AuthContext);
    var showVotersVoted = authContext.isAuthorized(true, authContext.tenantId, IPermissions.MONITOR_VOTERS_WHO_VOTED);
    var showTotalEnrolledVoters = authContext.isAuthorized(true, authContext.tenantId, IPermissions.MONITOR_ENROLLED_OVERSEAS_VOTERS);
    var showAllApproveDisapproveVoters = authContext.isAuthorized(true, authContext.tenantId, IPermissions.MONITOR_ALL_APPROVE_DISAPPROVE_VOTERS);
    var showManuallyApproveDisapproveVoters = authContext.isAuthorized(true, authContext.tenantId, IPermissions.MONITOR_MANUALLY_APPROVE_DISAPPROVE_VOTERS);
    var showAutomaticApproveDisapproveVoters = authContext.isAuthorized(true, authContext.tenantId, IPermissions.MONITOR_AUTOMATIC_APPROVE_DISAPPROVE_VOTERS);
    var showAuthenticatedVoters = authContext.isAuthorized(true, authContext.tenantId, IPermissions.MONITOR_AUTHENTICATED_VOTERS);
    var votersSection = useMemo(function () {
        var total_auth_errors = 0;
        if (typeof authenticationStats.invalidUsersErrorsCount === "number" &&
            typeof authenticationStats.invalidPasswordErrorsCount === "number") {
            total_auth_errors =
                authenticationStats.invalidUsersErrorsCount +
                    authenticationStats.invalidPasswordErrorsCount;
        }
        return {
            show: showTotalEnrolledVoters ||
                showAllApproveDisapproveVoters ||
                showManuallyApproveDisapproveVoters ||
                showAutomaticApproveDisapproveVoters ||
                showAuthenticatedVoters,
            title: t("monitoringDashboardScreen.voters.title"),
            stats: [
                {
                    show: showTotalEnrolledVoters,
                    title: t("monitoringDashboardScreen.voters.enrolledOverseasVoters"),
                    items: [
                        {
                            icon: _jsx(CheckCircleOutlineIcon, {}),
                            count: enrolledVotersCount,
                            percentage: calcPrecentage(enrolledVotersCount, eligibleVotersCount),
                        },
                    ],
                },
                {
                    show: showAllApproveDisapproveVoters,
                    title: t("monitoringDashboardScreen.voters.approvalStatus"),
                    items: [
                        {
                            icon: _jsx(CheckCircleOutlineIcon, {}),
                            count: approvalStats.approvedCount,
                            percentage: calcPrecentage(approvalStats.approvedCount, calcTotalApplications(approvalStats.approvedCount, approvalStats.disapprovedCount)),
                        },
                        {
                            icon: _jsx(CancelOutlinedIcon, {}),
                            count: approvalStats.disapprovedCount,
                            percentage: calcPrecentage(approvalStats.disapprovedCount, calcTotalApplications(approvalStats.approvedCount, approvalStats.disapprovedCount)),
                        },
                    ],
                },
                {
                    show: showManuallyApproveDisapproveVoters,
                    title: t("monitoringDashboardScreen.voters.manuallyApproval"),
                    items: [
                        {
                            icon: _jsx(CheckCircleOutlineIcon, {}),
                            count: approvalStats.manualApprovedCount,
                            percentage: calcPrecentage(approvalStats.manualApprovedCount, calcTotalApplications(approvalStats.manualApprovedCount, approvalStats.manualDisapprovedCount)),
                        },
                        {
                            icon: _jsx(CancelOutlinedIcon, {}),
                            count: approvalStats.manualDisapprovedCount,
                            percentage: calcPrecentage(approvalStats.manualDisapprovedCount, calcTotalApplications(approvalStats.manualApprovedCount, approvalStats.manualDisapprovedCount)),
                        },
                    ],
                },
                {
                    show: showAutomaticApproveDisapproveVoters,
                    title: t("monitoringDashboardScreen.voters.automaticallyApproval"),
                    items: [
                        {
                            icon: _jsx(CheckCircleOutlineIcon, {}),
                            count: approvalStats.automatedApprovedCount,
                            percentage: calcPrecentage(approvalStats.automatedApprovedCount, calcTotalApplications(approvalStats.automatedApprovedCount, approvalStats.automatedDisapprovedCount)),
                        },
                        {
                            icon: _jsx(CancelOutlinedIcon, {}),
                            count: approvalStats.automatedDisapprovedCount,
                            percentage: calcPrecentage(approvalStats.automatedDisapprovedCount, calcTotalApplications(approvalStats.automatedApprovedCount, approvalStats.automatedDisapprovedCount)),
                        },
                    ],
                },
                {
                    show: showAuthenticatedVoters,
                    title: t("monitoringDashboardScreen.voters.authenticatedVoters"),
                    items: [
                        {
                            icon: _jsx(CheckCircleOutlineIcon, {}),
                            count: authenticationStats.authenticatedCount,
                            percentage: calcPrecentage(authenticationStats.authenticatedCount, enrolledVotersCount),
                        },
                        {
                            icon: _jsx(CancelOutlinedIcon, {}),
                            count: authenticationStats.notAuthenticatedCount,
                            percentage: calcPrecentage(authenticationStats.notAuthenticatedCount, enrolledVotersCount),
                        },
                    ],
                },
            ],
        };
    }, [authenticationStats, enrolledVotersCount, approvalStats]);
    var pollsSection = useMemo(function () {
        return {
            show: showVotersVoted,
            title: t("monitoringDashboardScreen.polls.title"),
            stats: [
                {
                    show: showVotersVoted,
                    title: t("monitoringDashboardScreen.polls.voterTurnout"),
                    items: [
                        {
                            icon: _jsx(MarkEmailReadOutlinedIcon, {}),
                            count: votedCount,
                            percentage: calcPrecentage(votedCount, eligibleVotersCount),
                        },
                    ],
                },
            ],
        };
    }, [votedCount, eligibleVotersCount]);
    var statsSections = useMemo(function () { return [votersSection, pollsSection]; }, [votersSection, pollsSection]);
    return (_jsx(_Fragment, { children: _jsx(Stats, { statsSections: statsSections }) }));
};
export default ElectionStats;
